import { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { computed } from 'mobx';
import { useObserver } from 'mobx-react-lite';

import type { Dispensaries } from 'src/gql/graphql';
import CartItem from 'src/state/cart-item';
import useUser from 'hooks/use-user';
import useUI from 'hooks/use-ui';
import useCart from 'hooks/use-cart';
import useCurrentDispensary from 'src/dispensary/hooks/use-current-dispensary';
import useDispensary from 'src/dispensary/hooks/use-dispensary';

import type {
  Cart as RemoteCart,
  CartItem as RemoteCartItem,
  User as RemoteUser,
  CommerceComponentsDataInterface,
} from '@dutchiesdk/ecommerce-extensions-sdk';

import CartStore from 'src/state/cart';
import UserStore from 'src/state/user';

import { useBridgeActions } from './bridge-actions';
import { useBridgeDataLoaders } from './bridge-data-loaders';
import { mapDispensaryToRemote } from './bridge-map-dispensary';

export type CommerceComponentsDataInterfaceWithAuthentication = CommerceComponentsDataInterface & {
  isAuthenticated: boolean;
};

type UnknownFunction = (...args: unknown[]) => unknown;

const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

/**
 * Bridge action and loader hooks currently build fresh function collections on each render. Keep
 * the SDK-facing collection and function references stable while delegating every call to the
 * latest hook result, so an unrelated Apollo cache broadcast cannot fan out through context.
 */
const useStableFunctionMap = <T extends object>(functions: T): T => {
  const latestFunctions = useRef(functions);

  // Publishing during render can expose closures from an abandoned concurrent render to the
  // currently committed UI. Swap implementations only after React commits the matching state.
  useIsomorphicLayoutEffect(() => {
    latestFunctions.current = functions;
  }, [functions]);

  return useMemo(
    () =>
      Object.fromEntries(
        Object.entries(functions).map(([key]) => [
          key,
          (...args: unknown[]) => {
            // Do not close over the entry's initial value: this stable delegate must call the
            // implementation from the latest committed function map.
            const currentFunction = (latestFunctions.current as Record<string, UnknownFunction>)[key];
            return currentFunction(...args);
          },
        ])
      ) as T,
    // SDK action and data-loader keys are a static contract. Their implementations are read from
    // latestFunctions so router, dispensary, and query changes never leave stale closures behind.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
};

export const useBridgeDataInterface = (): CommerceComponentsDataInterfaceWithAuthentication => {
  const { dispensary: currentDispensary } = useCurrentDispensary();
  const { dispensary: legacyDispensary } = useDispensary() as { dispensary?: Dispensaries | undefined };
  const dispensary = currentDispensary ?? legacyDispensary;
  const User = useUser();
  const UI = useUI();
  const Cart = useCart();
  const actions = useStableFunctionMap(useBridgeActions());
  const dataLoaders = useStableFunctionMap(useBridgeDataLoaders());

  // MobX computed values retain their mapped object identity until an observed cart/user field
  // actually changes. Without this layer, any parent or query-store render creates new objects and
  // broadcasts context to every remote component even when the bridge data is identical.
  const mappedCart = useMemo(() => computed(() => (Cart ? mapCartToRemote(Cart) : undefined)), [Cart]);
  const cart = useObserver(() => mappedCart.get());

  // `User` is a singleton that's truthy for guests, so the authoritative `isLoggedIn` state must
  // drive both fields and keep `user` undefined whenever the session is unauthenticated.
  const mappedAuthentication = useMemo(
    () =>
      computed(() => {
        const isAuthenticated = User?.isLoggedIn ?? false;

        return {
          isAuthenticated,
          user: isAuthenticated && User ? mapUserToRemote(User) : undefined,
        };
      }),
    [User]
  );
  const authentication = useObserver(() => mappedAuthentication.get());
  const location = useMemo(() => mapDispensaryToRemote(dispensary), [dispensary]);
  const menuContext = useObserver(() => (UI.isKiosk ? 'kiosk' : 'store-front'));

  return useMemo(
    () => ({
      actions,
      cart,
      dataLoaders,
      isAuthenticated: authentication.isAuthenticated,
      location,
      menuContext,
      user: authentication.user,
    }),
    [actions, authentication, cart, dataLoaders, location, menuContext]
  );
};

const mapCartItem = (item: CartItem): RemoteCartItem => ({
  name: item.product?.name,
  price: item.price,
  // @ts-expect-error product.id is Maybe<string>; resolve when tightening SDK types.
  productId: item.product?.id,
  quantity: item.quantity,
});

const mapCartToRemote = (cart: CartStore): RemoteCart => ({
  // @ts-expect-error costBreakdown may be undefined vs RemoteCart; decide defaults before tightening types.
  discount: cart.costBreakdown.credit,
  items: Object.values(cart.items).map(mapCartItem),
  // @ts-expect-error costBreakdown may be undefined vs RemoteCart; decide defaults before tightening types.
  subtotal: cart.costBreakdown.subtotal,
  // @ts-expect-error costBreakdown may be undefined vs RemoteCart; decide defaults before tightening types.
  tax: cart.costBreakdown.tax,
  // @ts-expect-error costBreakdown may be undefined vs RemoteCart; decide defaults before tightening types.
  total: cart.costBreakdown.total,
});

const mapUserToRemote = (user: UserStore): RemoteUser => ({
  birthday: user.birthday,
  email: user.email ?? '',
  firstName: user.firstName,
  lastName: user.lastName,
});
